/** Hilfen für In-App-Benachrichtigungen: Store-Abgleich, Sprungziele, Gelesen-Markierung. */
import { compareSnowflakes, type MessageDto, type NotificationDto } from '@lagerfeuer/shared';
import { notificationsApi } from '../../lib/endpoints';
import { paths, pathForChannel } from '../../lib/routes';
import { appState } from '../../store/app';
import type { AppData } from '../../store/types';

/** Geladene Seiten in den Store übernehmen (Store bleibt die Quelle für Gelesen-Status und Zähler). */
export function mergeNotifications(list: NotificationDto[]): void {
  if (list.length === 0) return;
  appState().update((d) => {
    const byId = new Map(d.notifications.map((n) => [n.id, n]));
    for (const n of list) {
      const existing = byId.get(n.id);
      // Lokal bereits als gelesen markiert → nicht durch eine ältere Antwort zurücksetzen.
      if (existing) Object.assign(existing, { ...n, readAt: existing.readAt ?? n.readAt });
      else d.notifications.push(n);
    }
    d.notifications.sort((a, b) => compareSnowflakes(b.id, a.id));
  });
}

/** Als gelesen markieren: lokal sofort (wie NOTIFICATION_READ), dann POST /notifications/read. */
export async function markNotificationsRead(ids: string[] | 'all'): Promise<void> {
  if (ids !== 'all' && ids.length === 0) return;
  appState().dispatch('NOTIFICATION_READ', { ids });
  await notificationsApi.markRead(ids === 'all' ? { all: true } : { ids });
}

export async function deleteNotification(n: NotificationDto): Promise<void> {
  await notificationsApi.remove(n.id);
  appState().update((d) => {
    const had = d.notifications.find((x) => x.id === n.id);
    d.notifications = d.notifications.filter((x) => x.id !== n.id);
    if (had && !had.readAt) d.unreadNotifications = Math.max(0, d.unreadNotifications - 1);
  });
}

/** App-Pfad zum Ziel einer Benachrichtigung (Nachricht/Kanal) oder null. */
export function notificationTarget(s: Pick<AppData, 'channels'>, n: Pick<NotificationDto, 'channelId' | 'serverId' | 'messageId' | 'data'>): string | null {
  const messageId = n.messageId ?? undefined;
  if (n.channelId) {
    const ch = s.channels[n.channelId];
    if (ch) return pathForChannel(ch, messageId);
    return n.serverId ? paths.channel(n.serverId, n.channelId, messageId) : paths.dm(n.channelId, messageId);
  }
  const url = typeof n.data?.url === 'string' ? n.data.url : null;
  return url && url.startsWith('/') && !url.startsWith('//') ? url : null;
}

/** App-Pfad zu einer Erwähnung (Nachricht). */
export function messageTarget(s: Pick<AppData, 'channels'>, m: Pick<MessageDto, 'id' | 'channelId' | 'serverId'>): string {
  const ch = s.channels[m.channelId];
  if (ch) return pathForChannel(ch, m.id);
  return m.serverId ? paths.channel(m.serverId, m.channelId, m.id) : paths.dm(m.channelId, m.id);
}

export function relativeTime(iso: string, now = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return days === 1 ? 'gestern' : `vor ${days} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}
